import React, { useState, useCallback } from 'react';
import {
    Lock, Unlock, RefreshCw, ChevronDown, ChevronRight,
    Eye, ClipboardList, EyeOff, Construction, Users,
    UserPlus, UserMinus
} from 'lucide-react';
import './AdminPlatformManagement.css';
import { usePlatformConfig, QK } from '../../hooks/useAdminQueries';
import { useQueryClient } from '@tanstack/react-query';
import FreeConfigAdmin from './free/FreeConfigAdmin';
import FreeQuestionsAdmin from './free/FreeQuestionsAdmin';
import { NAV_STRUCTURE, TabDef, SubTabDef, TabsConfigMap, TabConfig } from '../../config/platformNavStructure';

const API_BASE_URL = '/api';

interface AdminPlatformManagementProps {
    onShowToast: (msg: string, type: 'success' | 'error') => void;
}

type PlatformTab = 'tabs' | 'free-content' | 'free-questions';

interface AllowedUser {
    _id: string;
    username: string;
    email: string;
}

// ── Componente de painel de acesso individual ─────────────────
const AllowedUsersPanel: React.FC<{
    tabKey: string;
    tabLabel: string;
    onShowToast: (msg: string, type: 'success' | 'error') => void;
    onUsersChanged: () => void;
}> = ({ tabKey, tabLabel, onShowToast, onUsersChanged }) => {
    const [users, setUsers] = useState<AllowedUser[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [identifier, setIdentifier] = useState('');
    const [adding, setAdding] = useState(false);
    const [removingId, setRemovingId] = useState<string | null>(null);
    const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
    const [searchError, setSearchError] = useState('');

    const load = useCallback(async () => {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`${API_BASE_URL}/platform-config/tabs/${encodeURIComponent(tabKey)}/allowed-users`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
            const d = await res.json();
            setUsers(d.users || []);
        }
        setLoaded(true);
    }, [tabKey]);

    React.useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        if (!identifier.trim()) return;
        setAdding(true);
        setSearchError('');
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/platform-config/tabs/${encodeURIComponent(tabKey)}/allowed-users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ identifier: identifier.trim() })
            });
            const d = await res.json();
            if (res.ok) {
                setUsers(prev => [...prev, d.user]);
                setIdentifier('');
                onShowToast(`${d.user.username} adicionado com acesso especial`, 'success');
                onUsersChanged();
            } else if (res.status === 404) {
                setSearchError('Usuário não encontrado');
            } else if (res.status === 409) {
                setSearchError('Usuário já tem acesso');
            } else {
                setSearchError(d.message || 'Erro ao adicionar');
            }
        } catch {
            setSearchError('Erro de conexão');
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = async (userId: string) => {
        if (confirmRemoveId !== userId) {
            setConfirmRemoveId(userId);
            setTimeout(() => setConfirmRemoveId(null), 2500);
            return;
        }
        setRemovingId(userId);
        setConfirmRemoveId(null);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(
                `${API_BASE_URL}/platform-config/tabs/${encodeURIComponent(tabKey)}/allowed-users/${userId}`,
                { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.ok) {
                setUsers(prev => prev.filter(u => u._id !== userId));
                onShowToast('Usuário removido', 'success');
                onUsersChanged();
            } else {
                onShowToast('Erro ao remover usuário', 'error');
            }
        } catch {
            onShowToast('Erro de conexão', 'error');
        } finally {
            setRemovingId(null);
        }
    };

    return (
        <div className="apm-allowed-panel">
            <div className="apm-allowed-header">
                <Users size={14} />
                <span>Acesso individual a <strong>{tabLabel}</strong></span>
            </div>
            <p className="apm-allowed-info">
                Esses usuários veem e acessam esta aba independentemente de estar oculta ou interditada.
            </p>

            <div className="apm-allowed-add-row">
                <input
                    className={`apm-allowed-input ${searchError ? 'error' : ''}`}
                    placeholder="Email ou username do usuário"
                    value={identifier}
                    onChange={e => { setIdentifier(e.target.value); setSearchError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                />
                <button className="apm-allowed-add-btn" onClick={handleAdd} disabled={adding || !identifier.trim()}>
                    {adding ? <RefreshCw size={13} className="spinning" /> : <UserPlus size={13} />}
                    Adicionar
                </button>
            </div>
            {searchError && <p className="apm-allowed-error">{searchError}</p>}

            {!loaded ? (
                <div className="apm-allowed-loading"><RefreshCw size={14} className="spinning" /></div>
            ) : users.length === 0 ? (
                <p className="apm-allowed-empty">Nenhum usuário com acesso especial.</p>
            ) : (
                <div className="apm-allowed-list">
                    {users.map(u => (
                        <div key={u._id} className="apm-allowed-user-row">
                            <div className="apm-allowed-avatar">{u.username[0]?.toUpperCase()}</div>
                            <div className="apm-allowed-user-info">
                                <span className="apm-allowed-username">{u.username}</span>
                                <span className="apm-allowed-email">{u.email}</span>
                            </div>
                            <button
                                className={`apm-allowed-remove-btn ${confirmRemoveId === u._id ? 'confirm' : ''}`}
                                onClick={() => handleRemove(u._id)}
                                disabled={removingId === u._id}
                            >
                                {removingId === u._id
                                    ? <RefreshCw size={12} className="spinning" />
                                    : confirmRemoveId === u._id
                                        ? 'Confirmar?'
                                        : <><UserMinus size={12} /> Remover</>
                                }
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Componente principal ──────────────────────────────────────
const AdminPlatformManagement: React.FC<AdminPlatformManagementProps> = ({ onShowToast }) => {
    const [activeTab, setActiveTab] = useState<PlatformTab>('tabs');
    const [updating, setUpdating] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [usersPanel, setUsersPanel] = useState<string | null>(null);
    const [usersCounts, setUsersCounts] = useState<Record<string, number>>({});

    const { data, isLoading } = usePlatformConfig();
    const tabsConfig: TabsConfigMap = (data?.tabsConfig as TabsConfigMap) ?? {};
    const qc = useQueryClient();

    // Carrega contagens de usuários ao montar
    React.useEffect(() => {
        if (!data) return;
        const tc: TabsConfigMap = (data?.tabsConfig as TabsConfigMap) ?? {};
        const counts: Record<string, number> = {};
        for (const [k, v] of Object.entries(tc)) {
            counts[k] = (v as TabConfig)?.allowedUsers?.length ?? 0;
        }
        setUsersCounts(counts);
    }, [data]);

    const getTabCfg = (key: string): TabConfig =>
        tabsConfig[key] ?? { hidden: false, interdicted: false, allowedUsers: [] };

    // ── Toggle hidden ou interdicted ──────────────────────────
    const patchTab = async (tabKey: string, patch: { hidden?: boolean; interdicted?: boolean }) => {
        setUpdating(tabKey + JSON.stringify(patch));
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/platform-config/tabs/${encodeURIComponent(tabKey)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(patch)
            });
            if (res.ok) {
                qc.invalidateQueries({ queryKey: QK.platformConfig });
                const label = patch.hidden !== undefined ? 'Visibilidade' : 'Interdição';
                onShowToast(`${label} atualizada`, 'success');
            } else throw new Error();
        } catch {
            onShowToast('Erro ao atualizar', 'error');
        } finally {
            setUpdating(null);
        }
    };

    // Endpoint legado para subabas (interdictedTabs)
    const toggleSubtab = async (key: string, current: boolean) => {
        setUpdating(key);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/platform-config/interdicted-tabs`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ tabName: key, interdicted: !current })
            });
            if (res.ok) {
                qc.invalidateQueries({ queryKey: QK.platformConfig });
                onShowToast(!current ? 'Subaba interditada' : 'Subaba liberada', 'success');
            } else throw new Error();
        } catch {
            onShowToast('Erro ao atualizar', 'error');
        } finally {
            setUpdating(null);
        }
    };

    const toggleExpanded = (key: string) =>
        setExpanded(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });

    const toggleUsersPanel = (key: string) =>
        setUsersPanel(prev => prev === key ? null : key);

    // ── Métricas do resumo ────────────────────────────────────
    const hiddenCount = Object.values(tabsConfig).filter((c: any) => c?.hidden).length;
    const interdictedCount = Object.values(tabsConfig).filter((c: any) => c?.interdicted).length;
    const specialCount = Object.values(usersCounts).reduce((a, b) => a + b, 0);

    const topTabs = [
        { id: 'tabs' as PlatformTab, label: 'Gerenciamento de Abas', icon: Construction },
        { id: 'free-content' as PlatformTab, label: 'Conteúdo Gratuito', icon: Eye },
        { id: 'free-questions' as PlatformTab, label: 'Questões Gratuitas', icon: ClipboardList },
    ];

    const interdictedLegacy: Record<string, boolean> = data?.interdictedTabs ?? {};

    return (
        <div className="adm-plat-container adm-page">
            <div className="adm-page-header">
                <h2 className="adm-page-title">Gerenciamento da Plataforma</h2>
                <p className="adm-page-subtitle">Controle de acesso e configurações de conteúdo gratuito</p>
            </div>

            <div className="adm-tabs">
                {topTabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button key={tab.id} className={`adm-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                            <Icon size={18} strokeWidth={1.5} />
                            <span>{tab.label}</span>
                        </button>
                    );
                })}
            </div>

            <div className="sales-content">

                {/* ── ABA: GERENCIAMENTO DE ABAS ── */}
                {activeTab === 'tabs' && (
                    isLoading ? (
                        <div className="adm-plat-loading">
                            <RefreshCw className="spinning" size={32} />
                            <p>Carregando configuração...</p>
                        </div>
                    ) : (
                        <div className="adm-plat-inner">

                            {/* Resumo */}
                            <div className="apm-summary">
                                <div className="apm-summary-card hidden-card">
                                    <EyeOff size={16} />
                                    <span><strong>{hiddenCount}</strong> aba{hiddenCount !== 1 ? 's' : ''} oculta{hiddenCount !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="apm-summary-card interdicted-card">
                                    <Construction size={16} />
                                    <span><strong>{interdictedCount}</strong> interditada{interdictedCount !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="apm-summary-card access-card">
                                    <Users size={16} />
                                    <span><strong>{specialCount}</strong> acesso{specialCount !== 1 ? 's' : ''} especial{specialCount !== 1 ? 'is' : ''}</span>
                                </div>
                            </div>

                            <div className="adm-plat-section-header">
                                <h2>Abas do Dashboard</h2>
                                <p className="adm-plat-desc">
                                    Gerencie visibilidade, interdição e acesso individual por aba. Você (admin) mantém acesso total independente do status.
                                </p>
                            </div>

                            <div className="adm-plat-tabs-list">
                                {NAV_STRUCTURE.map((tab: TabDef) => {
                                    const cfg = getTabCfg(tab.key);
                                    const isExpanded = expanded.has(tab.key);
                                    const hasSubtabs = (tab.subtabs?.length ?? 0) > 0;
                                    const usersCount = usersCounts[tab.key] ?? cfg.allowedUsers?.length ?? 0;
                                    const isUsersPanelOpen = usersPanel === tab.key;

                                    const rowClass = [
                                        'adm-plat-tab-row',
                                        cfg.hidden ? 'is-hidden' : '',
                                        cfg.interdicted ? 'is-interdicted' : '',
                                    ].filter(Boolean).join(' ');

                                    return (
                                        <div key={tab.key} className={rowClass}>
                                            <div className="adm-plat-tab-main">
                                                {hasSubtabs ? (
                                                    <button className="adm-plat-expand-btn" onClick={() => toggleExpanded(tab.key)}>
                                                        {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                                                    </button>
                                                ) : (
                                                    <div className="adm-plat-expand-placeholder" />
                                                )}

                                                <div className="adm-plat-tab-info">
                                                    <div className="adm-plat-tab-title-row">
                                                        <h3>{tab.label}</h3>
                                                        <TabStatusBadge cfg={cfg} usersCount={usersCount} />
                                                    </div>
                                                    <p>{tab.description}</p>
                                                </div>

                                                <div className="adm-plat-tab-status apm-controls">
                                                    {/* Toggle Hidden */}
                                                    <div className="apm-toggle-group">
                                                        <span className="apm-toggle-label">Ocultar</span>
                                                        <button
                                                            className={`apm-toggle-btn ${cfg.hidden ? 'active-hidden' : ''}`}
                                                            onClick={() => patchTab(tab.key, { hidden: !cfg.hidden })}
                                                            disabled={updating !== null}
                                                            title={cfg.hidden ? 'Clique para tornar visível' : 'Clique para ocultar'}
                                                        >
                                                            {updating?.startsWith(tab.key + '{"hidden"')
                                                                ? <RefreshCw size={12} className="spinning" />
                                                                : cfg.hidden ? <EyeOff size={12} /> : <Eye size={12} />
                                                            }
                                                        </button>
                                                    </div>

                                                    {/* Toggle Interditar */}
                                                    <div className="apm-toggle-group" title={cfg.hidden ? 'Aba já está oculta — interdição não se aplica' : ''}>
                                                        <span className="apm-toggle-label">Interditar</span>
                                                        <button
                                                            className={`apm-toggle-btn ${cfg.interdicted ? 'active-interdicted' : ''}`}
                                                            onClick={() => patchTab(tab.key, { interdicted: !cfg.interdicted })}
                                                            disabled={updating !== null || cfg.hidden}
                                                        >
                                                            {updating?.startsWith(tab.key + '{"interdicted"')
                                                                ? <RefreshCw size={12} className="spinning" />
                                                                : cfg.interdicted ? <Lock size={12} /> : <Unlock size={12} />
                                                            }
                                                        </button>
                                                    </div>

                                                    {/* Botão acesso individual */}
                                                    <button
                                                        className={`apm-users-btn ${isUsersPanelOpen ? 'open' : ''}`}
                                                        onClick={() => toggleUsersPanel(tab.key)}
                                                    >
                                                        <Users size={13} />
                                                        {usersCount > 0 ? `${usersCount} usuário${usersCount > 1 ? 's' : ''}` : 'Acesso'}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Painel de acesso individual */}
                                            {isUsersPanelOpen && (
                                                <AllowedUsersPanel
                                                    tabKey={tab.key}
                                                    tabLabel={tab.label}
                                                    onShowToast={onShowToast}
                                                    onUsersChanged={() => {
                                                        setUsersCounts(prev => {
                                                            qc.invalidateQueries({ queryKey: QK.platformConfig });
                                                            return prev; // será atualizado pelo re-render
                                                        });
                                                    }}
                                                />
                                            )}

                                            {/* Subabas */}
                                            {hasSubtabs && isExpanded && (
                                                <div className="adm-plat-subtabs">
                                                    {tab.subtabs!.map((st: SubTabDef, idx: number) => {
                                                        const stKey = `${tab.key}.${st.key}`;
                                                        const stBlocked = !!(interdictedLegacy[stKey]);
                                                        const isLast = idx === tab.subtabs!.length - 1;
                                                        const parentHidden = cfg.hidden;
                                                        const parentInterdicted = cfg.interdicted;

                                                        return (
                                                            <div
                                                                key={st.key}
                                                                className={[
                                                                    'adm-plat-subtab-row',
                                                                    stBlocked ? 'is-interdicted' : '',
                                                                    (parentHidden || parentInterdicted) ? 'parent-interdicted' : '',
                                                                    isLast ? 'is-last' : '',
                                                                ].filter(Boolean).join(' ')}
                                                            >
                                                                <div className={`adm-plat-tree-line ${isLast ? 'last' : ''}`} />
                                                                <div className="adm-plat-tree-branch" />
                                                                <div className="adm-plat-tab-info">
                                                                    <h4>{st.label}</h4>
                                                                    <p>{st.description}</p>
                                                                </div>
                                                                <div className="adm-plat-tab-status">
                                                                    {(parentHidden || parentInterdicted) ? (
                                                                        <span className="adm-plat-parent-note">
                                                                            <Lock size={12} />
                                                                            {parentHidden ? 'Aba oculta' : 'Aba interditada'}
                                                                        </span>
                                                                    ) : (
                                                                        <>
                                                                            <StatusBadge interdicted={stBlocked} />
                                                                            <ToggleBtn
                                                                                interdicted={stBlocked}
                                                                                loading={updating === stKey}
                                                                                onClick={() => toggleSubtab(stKey, stBlocked)}
                                                                            />
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )
                )}

                {activeTab === 'free-content' && <FreeConfigAdmin onShowToast={onShowToast} />}
                {activeTab === 'free-questions' && <FreeQuestionsAdmin onShowToast={onShowToast} />}
            </div>
        </div>
    );
};

// ── Sub-componentes ───────────────────────────────────────────

const TabStatusBadge: React.FC<{ cfg: TabConfig; usersCount: number }> = ({ cfg, usersCount }) => {
    if (cfg.hidden) {
        return (
            <span className="adm-plat-badge hidden-badge">
                <EyeOff size={11} /> Oculta{usersCount > 0 ? ` · 👥 ${usersCount}` : ''}
            </span>
        );
    }
    if (cfg.interdicted) {
        return (
            <span className="adm-plat-badge interdicted-badge">
                <Construction size={11} /> Em obras{usersCount > 0 ? ` · 👥 ${usersCount}` : ''}
            </span>
        );
    }
    if (usersCount > 0) {
        return (
            <span className="adm-plat-badge available-badge">
                <Unlock size={11} /> Disponível · 👥 {usersCount}
            </span>
        );
    }
    return (
        <span className="adm-plat-badge available-badge">
            <Unlock size={11} /> Disponível
        </span>
    );
};

const StatusBadge: React.FC<{ interdicted: boolean }> = ({ interdicted }) => (
    <span className={`adm-plat-badge ${interdicted ? 'interdicted-badge' : 'available-badge'}`}>
        {interdicted ? <><Lock size={12} /> Interditada</> : <><Unlock size={12} /> Disponível</>}
    </span>
);

const ToggleBtn: React.FC<{ interdicted: boolean; loading: boolean; onClick: () => void }> = ({ interdicted, loading, onClick }) => (
    <button className={`adm-plat-toggle-btn ${interdicted ? 'unlock' : 'lock'}`} onClick={onClick} disabled={loading}>
        {loading
            ? <RefreshCw size={14} className="spinning" />
            : interdicted ? <><Unlock size={14} /> Liberar</> : <><Lock size={14} /> Interditar</>
        }
    </button>
);

export default AdminPlatformManagement;
